<template>
  <div class="fang-market-panel">
    <div class="market-layout">
      <aside class="market-side-nav" aria-label="坊市导航">
        <button
          v-for="tab in tabs"
          :key="tab.key"
          type="button"
          :class="['tab-btn', { active: currentTab === tab.key }]"
          @click="currentTab = tab.key"
        >
          <span>{{ tab.label }}</span>
          <em v-if="tab.key === 'market'">{{ filteredMarketTotal }}</em>
          <em v-else-if="tab.key === 'my'">{{ activeStalls.length }}</em>
          <em v-else-if="tab.key === 'buy'">{{ buyOrderList.length }}</em>
          <em v-else-if="tab.key === 'exchange'">{{ myPostedOrderList.length + myAcceptedOrderList.length }}</em>
        </button>
      </aside>

      <section class="market-content">
        <div v-if="currentTab !== 'daily' && currentTab !== 'treasure'" class="market-content-head">
          <div>
            <h4>{{ currentTabLabel }}</h4>
            <span v-if="currentTab === 'market'">{{ filteredMarketTotal }} 件寄售物品 · 第 {{ marketPage }} / {{ marketTotalPages }} 页</span>
            <span v-else-if="currentTab === 'my'">{{ activeStalls.length }} / {{ maxSlots }} 个摊位已使用</span>
            <span v-else-if="currentTab === 'daily'">每日刷新，可购买 {{ currentTab === 'daily' ? '商店商品' : '' }}</span>
            <span v-else-if="currentTab === 'treasure'">分类兑换与合成，可直接消耗材料</span>
            <span v-else-if="currentTab === 'buy'">公开换物单 {{ buyOrderList.length }} 条</span>
            <span v-else>管理已发布与已揭榜的换物单</span>
          </div>
          <div class="market-content-actions">
            <div class="market-stat market-stat--inline">
              <span>活跃摊位</span><strong>{{ activeStalls.length }}/{{ maxSlots }}</strong>
            </div>
            <div class="market-stat market-stat--inline">
              <span>今日收益</span><strong>{{ marketProfitInfo.currentProfit }}/{{ marketProfitInfo.dailyLimit }}</strong>
            </div>
            <button type="button" class="market-action-button" :disabled="marketLoading" @click="refreshCurrentTab">
            {{ marketLoading ? '刷新中...' : '刷新' }}
            </button>
          </div>
        </div>

        <div v-if="currentTab === 'market'" class="market-section">
          <div class="market-filter-bar">
            <div class="market-filter-row">
              <div class="market-filter-group market-filter-group--search">
                <span class="market-filter-label">搜索</span>
                <input
                  v-model.trim="marketKeyword"
                  class="market-filter-input"
                  type="text"
                  placeholder="装备、物品或卖家"
                />
              </div>
              <div class="market-filter-group market-filter-group--level">
                <span class="market-filter-label">最低等级</span>
                <input
                  v-model="minLevelInput"
                  class="market-filter-input market-filter-input--level"
                  type="number"
                  min="0"
                  step="1"
                  placeholder="不限"
                />
              </div>
              <button type="button" class="market-filter-reset" :disabled="marketLoading" @click="resetMarketFilters">重置</button>
            </div>
            <div class="market-filter-row">
              <div class="market-filter-group">
                <span class="market-filter-label">元素</span>
                <div class="element-filter-list">
                  <button
                    v-for="option in elementOptions"
                    :key="option.value"
                    type="button"
                    :class="['element-filter-chip', { active: selectedElement === option.value }]"
                    @click="selectedElement = option.value"
                  >
                    {{ option.label }}
                  </button>
                </div>
              </div>
              <div class="market-filter-group">
                <span class="market-filter-label">部位</span>
                <div class="element-filter-list">
                  <button
                    v-for="option in kindOptions"
                    :key="option.value"
                    type="button"
                    :class="['element-filter-chip', { active: selectedKind === option.value }]"
                    @click="selectedKind = option.value"
                  >
                    {{ option.label }}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div class="market-grid">
            <article
              v-for="item in paginatedMarketList"
              :key="item.busId"
              class="market-card"
              :class="{ 'is-equip': isEquipItem(item), 'is-xinfa': isWildXinfaItem(item) }"
            >
                <div class="market-card__icon" :style="{ color: marketItemNameColor(item) }">
                  {{ (item.busName || item.goodName || '?').slice(0, 1) }}
                </div>
                <div class="market-card__main">
                  <div class="market-card__top">
                    <Tag size="small" :color="marketItemTagColor(item)">{{ marketItemTagText(item) }}</Tag>
                    <span class="market-card__seller">{{ item.sellerName || '匿名' }}</span>
                  </div>
                  <div class="market-card__name" :style="{ color: marketItemNameColor(item) }">
                    {{ item.busName || item.goodName }}
                  </div>
                  <div v-if="marketItemMeta(item)" class="market-card__meta">{{ marketItemMeta(item) }}</div>
                  <div v-if="marketItemStats(item)" class="market-card__stats">{{ marketItemStats(item) }}</div>
                  <div v-if="marketItemSuit(item) || marketItemAffixes(item).length" class="market-card__suitline">
                    <span v-if="marketItemSuit(item)" class="market-card__suit">{{ marketItemSuit(item) }}</span>
                    <span
                      v-for="(affix, affixIndex) in marketItemAffixes(item)"
                      :key="affixIndex"
                    >【{{ affix.affixLevel || '词缀' }}】{{ affix.affixDescribe || affix.affixName }}</span>
                  </div>
                  <div v-if="getPublicRemainSeconds(item) > 0" class="market-card__meta market-card__meta--warn">
                    公示剩余 {{ formatPublicRemain(getPublicRemainSeconds(item)) }}
                  </div>
                </div>
                <div class="market-card__side">
                  <strong class="market-card__price">{{ item.price }} 灵石</strong>
                  <div class="market-card__actions">
                    <button type="button" class="market-card__view" @click.stop="openMarketDetail(item)">查看</button>
                    <button type="button" :disabled="marketLoading" @click.stop="$emit('buy-item', item)">购买</button>
                  </div>
                </div>
              </article>
            <div v-if="filteredMarketList.length === 0" class="empty-state grid-empty">暂无符合筛选条件的寄售物品</div>
          </div>

          <div v-if="filteredMarketTotal > marketPageSize" class="market-pagination">
            <button type="button" :disabled="marketLoading || marketPage <= 1" @click="changeMarketPage(marketPage - 1)">上一页</button>
            <span class="market-pagination__text">第 {{ marketPage }} / {{ marketTotalPages }} 页</span>
            <button type="button" :disabled="marketLoading || marketPage >= marketTotalPages" @click="changeMarketPage(marketPage + 1)">下一页</button>
          </div>
        </div>

        <div v-else-if="currentTab === 'daily' || currentTab === 'treasure'" class="embedded-commerce-panel">
          <CaveFarmMarket
            ref="embeddedCommerce"
            :initial-tab="currentTab"
            :hide-tabs="true"
            @purchased="$emit('commerce-updated')"
            @synthesized="$emit('commerce-updated')"
          />
        </div>

        <div v-else-if="currentTab === 'my'" class="stall-grid">
          <article
            v-for="slot in slotCards"
            :key="slot.slot"
            class="stall-card"
            :class="{ 'stall-card--empty': !slot.item }"
            @click="handleSlotClick(slot)"
          >
            <div class="stall-card__slot">摊位 {{ slot.slot }}</div>
            <template v-if="slot.item">
              <div class="stall-card__icon" :style="{ color: marketItemNameColor(slot.item) }">
                {{ (slot.item.busName || slot.item.goodName || '?').slice(0, 1) }}
              </div>
              <div class="stall-card__main">
                <strong class="stall-name">{{ slot.item.busName || slot.item.goodName }}</strong>
                <span v-if="marketItemMeta(slot.item)" class="stall-meta">{{ marketItemMeta(slot.item) }}</span>
                <strong class="stall-price">{{ slot.item.price }} 灵石</strong>
              </div>
              <button type="button" class="stop-bubble" :disabled="marketLoading" @click.stop="$emit('off-shelf', slot.item)">下架</button>
            </template>
            <template v-else>
              <div class="stall-empty">空摊位</div>
              <div class="stall-tip">点击上架物品</div>
            </template>
          </article>
        </div>

        <div v-else-if="currentTab === 'buy'" class="list-wrap">
          <div class="list-toolbar">
            <button type="button" class="market-primary-button" :disabled="marketLoading" @click="$emit('create-exchange-order')">发布换单</button>
          </div>
          <article v-for="item in buyOrderList" :key="item.orTaskId" class="trade-row">
            <div class="trade-icon">{{ (item.name || item.goodName || '?').slice(0, 1) }}</div>
            <div class="trade-main">
              <div class="trade-title">{{ item.name }}</div>
              <div class="trade-meta">求 {{ item.goodName }} x {{ item.goodNum }} · 换 {{ item.interchangeName || '未知物品' }} x {{ item.interchangeNum || 0 }}</div>
              <div class="trade-meta">押金 {{ item.price || 0 }} 灵石</div>
            </div>
            <div class="trade-side">
              <span class="trade-price">{{ item.price || 0 }} 灵石</span>
              <button type="button" :disabled="marketLoading" @click="$emit('unveil-buy-order', item)">揭榜</button>
            </div>
          </article>
          <div v-if="buyOrderList.length === 0" class="empty-state">当前没有换物单</div>
        </div>

        <div v-else-if="currentTab === 'exchange'" class="exchange-manage">
          <div class="exchange-block">
            <div class="exchange-block__title">我发布的换单 <span>{{ myPostedOrderList.length }}</span></div>
            <div v-if="myPostedOrderList.length" class="exchange-list">
              <article v-for="item in myPostedOrderList" :key="'posted-' + item.orTaskId" class="trade-row">
                <div class="trade-icon">{{ (item.name || '?').slice(0, 1) }}</div>
                <div class="trade-main">
                  <div class="trade-title">{{ item.name }}</div>
                  <div class="trade-meta">求 {{ item.goodName }} x {{ item.goodNum }} · 换 {{ item.interchangeName || '未知物品' }} x {{ item.interchangeNum || 0 }}</div>
                  <div class="trade-meta">状态 {{ formatOrderStatus(item.busStatus) }}</div>
                </div>
                <div class="trade-side">
                  <span class="trade-price">{{ item.price || 0 }} 灵石</span>
                  <button v-if="canClaimPostedOrder(item)" type="button" :disabled="marketLoading" @click="$emit('claim-buy-order', item)">领取</button>
                </div>
              </article>
            </div>
            <div v-else class="empty-state">暂无我发布的换单</div>
          </div>

          <div class="exchange-block">
            <div class="exchange-block__title">我揭榜的换单 <span>{{ myAcceptedOrderList.length }}</span></div>
            <div v-if="myAcceptedOrderList.length" class="exchange-list">
              <article v-for="item in myAcceptedOrderList" :key="'accepted-' + item.orTaskId" class="trade-row">
                <div class="trade-icon">{{ (item.name || '?').slice(0, 1) }}</div>
                <div class="trade-main">
                  <div class="trade-title">{{ item.name }}</div>
                  <div class="trade-meta">交付 {{ item.goodName }} x {{ item.goodNum }} · 可得 {{ item.interchangeName || '未知物品' }} x {{ item.interchangeNum || 0 }}</div>
                  <div class="trade-meta">状态 {{ formatOrderStatus(item.busStatus) }}</div>
                </div>
                <div class="trade-side">
                  <span class="trade-price">{{ item.price || 0 }} 灵石</span>
                  <button v-if="canDeliverAcceptedOrder(item)" type="button" :disabled="marketLoading" @click="$emit('deliver-buy-order', item)">交付</button>
                </div>
              </article>
            </div>
            <div v-else class="empty-state">暂无我揭榜的换单</div>
          </div>
        </div>
      </section>
    </div>

    <Modal
      v-model="marketDetailVisible"
      :title="marketDetailTitle"
      width="620"
      :footer-hide="true"
      :styles="{ top: '80px' }"
      class-name="fang-market-detail-modal"
    >
      <div v-if="selectedMarketItem" class="market-detail-content">
        <div class="market-detail-summary">
          <div class="market-detail-icon" :style="{ color: marketItemNameColor(selectedMarketItem) }">
            {{ (selectedMarketItem.busName || selectedMarketItem.goodName || '?').slice(0, 1) }}
          </div>
          <div>
            <h4 :style="{ color: marketItemNameColor(selectedMarketItem) }">{{ selectedMarketItem.busName || selectedMarketItem.goodName }}</h4>
            <p v-if="marketItemMeta(selectedMarketItem)">{{ marketItemMeta(selectedMarketItem) }}</p>
            <p>卖家 {{ selectedMarketItem.sellerName || '匿名' }} · {{ selectedMarketItem.price }} 灵石</p>
          </div>
        </div>

        <div v-if="isEquipItem(selectedMarketItem) && selectedMarketItem.packItemsVo" class="market-detail-section">
          <div class="market-detail-section__title">装备属性</div>
          <div class="market-detail-stat-grid">
            <div v-for="stat in marketDetailStats(selectedMarketItem.packItemsVo)" :key="stat.label" class="market-detail-stat">
              <span>{{ stat.label }}</span>
              <strong>{{ stat.value }}</strong>
            </div>
          </div>
          <div v-if="selectedMarketItem.packItemsVo.decs" class="market-detail-description">{{ selectedMarketItem.packItemsVo.decs }}</div>
          <div v-if="selectedMarketItem.packItemsVo.affixVoList && selectedMarketItem.packItemsVo.affixVoList.length" class="market-detail-section market-detail-section--nested">
            <div class="market-detail-section__title">附加词缀</div>
            <div class="market-detail-affixes">
              <div v-for="affix in selectedMarketItem.packItemsVo.affixVoList" :key="affix.affixName + affix.affixDescribe" class="market-detail-affix" :style="{ color: affixColor(affix.affixLevel) }">
                <strong>【{{ affix.affixLevel }}】{{ affix.affixName }}</strong>
                <span>{{ affix.affixDescribe }}</span>
              </div>
            </div>
          </div>
          <div v-if="selectedMarketItem.packItemsVo.suitName || selectedMarketItem.packItemsVo.suitDesc || selectedMarketItem.packItemsVo.suitOneDesc || selectedMarketItem.packItemsVo.suitTwoDesc || selectedMarketItem.packItemsVo.suitThreeDesc" class="market-detail-section market-detail-section--nested">
            <div class="market-detail-section__title">套装共鸣</div>
            <div v-if="selectedMarketItem.packItemsVo.suitName" class="market-detail-line">套装：{{ selectedMarketItem.packItemsVo.suitName }}</div>
            <div v-if="selectedMarketItem.packItemsVo.suitDesc" class="market-detail-line">{{ selectedMarketItem.packItemsVo.suitDesc }}</div>
            <div v-if="selectedMarketItem.packItemsVo.suitOneDesc" class="market-detail-line">{{ getSuitEffectPieceLabel(selectedMarketItem.packItemsVo.suitOneDesc, '2件') }}：{{ selectedMarketItem.packItemsVo.suitOneDesc }}</div>
            <div v-if="selectedMarketItem.packItemsVo.suitTwoDesc" class="market-detail-line">{{ getSuitEffectPieceLabel(selectedMarketItem.packItemsVo.suitTwoDesc, '4件') }}：{{ selectedMarketItem.packItemsVo.suitTwoDesc }}</div>
            <div v-if="selectedMarketItem.packItemsVo.suitThreeDesc" class="market-detail-line">{{ getSuitEffectPieceLabel(selectedMarketItem.packItemsVo.suitThreeDesc, '6件') }}：{{ selectedMarketItem.packItemsVo.suitThreeDesc }}</div>
          </div>
        </div>

        <div v-else-if="isWildXinfaItem(selectedMarketItem)" class="market-detail-section">
          <div class="market-detail-section__title">心法信息</div>
          <div class="market-detail-stat-grid">
            <div class="market-detail-stat"><span>层数</span><strong>{{ getWildXinfaInfo(selectedMarketItem).maxLayer || 0 }} 层</strong></div>
            <div class="market-detail-stat"><span>每层词条</span><strong>{{ getWildXinfaInfo(selectedMarketItem).affixCountPerLayer || 0 }} 条</strong></div>
            <div class="market-detail-stat"><span>状态</span><strong>{{ Number(getWildXinfaInfo(selectedMarketItem).identifyStatus) === 1 ? '已鉴定' : '未鉴定' }}</strong></div>
          </div>
          <div v-if="getWildXinfaInfo(selectedMarketItem).affixes && getWildXinfaInfo(selectedMarketItem).affixes.length" class="market-detail-section market-detail-section--nested">
            <div class="market-detail-section__title">心法词条</div>
            <div class="market-detail-xinfa-list">
              <div v-for="affix in getWildXinfaInfo(selectedMarketItem).affixes" :key="affix.affixId || (affix.layerNo + '-' + affix.affixDesc)" class="market-detail-xinfa-item" :class="{ 'market-detail-xinfa-item--locked': Number(affix.unlockStatus) !== 1 }">
                <div class="market-detail-xinfa-head"><strong>第{{ affix.layerNo || 0 }}层</strong><span>{{ Number(affix.unlockStatus) === 1 ? '已解锁' : '未解锁' }}</span></div>
                <div v-for="(line, lineIndex) in formatXinfaAffixLines(affix)" :key="lineIndex" class="market-detail-xinfa-line">{{ line }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  </div>
</template>

<script>
import CaveFarmMarket from './cave-farm/CaveFarmMarket.vue';

export default {
  name: "FangMarketSelectPanel",
  components: {
    CaveFarmMarket
  },
  props: {
    maxSlots: {
      type: Number,
      default: 5
    },
    activeStalls: {
      type: Array,
      default: () => []
    },
    marketList: {
      type: Array,
      default: () => []
    },
    marketLoading: {
      type: Boolean,
      default: false
    },
    marketTotal: {
      type: Number,
      default: 0
    },
    marketPage: {
      type: Number,
      default: 1
    },
    buyOrderList: {
      type: Array,
      default: () => []
    },
    myPostedOrderList: {
      type: Array,
      default: () => []
    },
    myAcceptedOrderList: {
      type: Array,
      default: () => []
    },
    marketProfitInfo: {
      type: Object,
      default: () => ({
        currentProfit: 0,
        dailyLimit: 0,
        remainProfit: 0
      })
    }
  },
  data() {
    return {
      currentTab: "market",
      marketKeyword: "",
      selectedElement: "all",
      selectedKind: "all",
      minLevelInput: "",
      marketPageSize: 6,
      nowTs: Date.now(),
      countdownTimer: null,
      publicCountdownMap: {},
      marketDetailVisible: false,
      selectedMarketItem: null,
      elementOptions: [
        { value: "all", label: "全部" },
        { value: "金", label: "金" },
        { value: "木", label: "木" },
        { value: "水", label: "水" },
        { value: "火", label: "火" },
        { value: "土", label: "土" },
        { value: "风", label: "风" },
        { value: "雷", label: "雷" },
        { value: "冰", label: "冰" }
      ],
      kindOptions: [
        { value: "all", label: "全部" },
        { value: 1, label: "武器" },
        { value: 2, label: "玄端" },
        { value: 3, label: "头冠" },
        { value: 4, label: "项链" },
        { value: 7, label: "戒指" },
        { value: 8, label: "绶佩" },
        { value: 9, label: "绶带" },
        { value: 10, label: "鞜履" },
        { value: 11, label: "法宝" }
      ],
      tabs: [
        { key: "daily", label: "每日商店" },
        { key: "treasure", label: "珍宝阁" },
        { key: "market", label: "坊市大厅" },
        { key: "my", label: "我的摊位" },
        { key: "buy", label: "换物榜" },
        { key: "exchange", label: "我的换单" }
      ]
    };
  },
  computed: {
    normalizedMinLevel() {
      const level = Number(this.minLevelInput);
      if (!Number.isFinite(level) || level <= 0) return 0;
      return Math.floor(level);
    },
    filteredMarketList() {
      const keyword = this.marketKeyword.toLowerCase();
      return this.marketList.filter((item) => {
        const equip = this.isEquipItem(item);
        const wildXinfa = this.isWildXinfaItem(item);
        const equipInfo = item.packItemsVo || {};
        const wildXinfaInfo = item.wildXinfaVo || {};
        const itemName = String(item.busName || item.goodName || "").toLowerCase();
        const sellerName = String(item.sellerName || "").toLowerCase();
        const equipName = String(equipInfo.itemName || "").toLowerCase();
        const xinfaName = String(wildXinfaInfo.xinfaName || "").toLowerCase();
        const xinfaAffixText = Array.isArray(wildXinfaInfo.affixes)
          ? wildXinfaInfo.affixes.map((affix) => String(affix.affixDesc || "")).join(" ").toLowerCase()
          : "";
        const elementName = String(equipInfo.weaponTypeDec || "").trim();
        const level = Number(equip ? (equipInfo.level || 0) : (wildXinfaInfo.maxLayer || 0));
        const kind = Number(equipInfo.kind || 0);

        const matchKeyword = !keyword ||
          itemName.includes(keyword) ||
          sellerName.includes(keyword) ||
          equipName.includes(keyword) ||
          xinfaName.includes(keyword) ||
          xinfaAffixText.includes(keyword);

        const matchElement = this.selectedElement === "all" ||
          (equip && elementName.includes(this.selectedElement));

        const matchLevel = this.normalizedMinLevel <= 0 ||
          ((equip || wildXinfa) && level >= this.normalizedMinLevel);

        const matchKind = this.selectedKind === "all" ||
          (equip && kind === this.selectedKind);

        return matchKeyword && matchElement && matchLevel && matchKind;
      });
    },
    filteredMarketTotal() {
      return this.filteredMarketList.length;
    },
    marketTotalPages() {
      return Math.max(1, Math.ceil(this.filteredMarketTotal / this.marketPageSize));
    },
    paginatedMarketList() {
      const start = (this.marketPage - 1) * this.marketPageSize;
      return this.filteredMarketList.slice(start, start + this.marketPageSize);
    },
    slotCards() {
      const result = [];
      for (let i = 1; i <= this.maxSlots; i++) {
        result.push({ slot: i, item: this.findStallBySlot(i) || null });
      }
      return result;
    },
    currentTabLabel() {
      const tab = this.tabs.find((item) => item.key === this.currentTab);
      return tab ? tab.label : "坊市大厅";
    },
    marketDetailTitle() {
      if (!this.selectedMarketItem) return "商品详情";
      return `查看 · ${this.selectedMarketItem.busName || this.selectedMarketItem.goodName || "商品"}`;
    }
  },
  watch: {
    currentTab() {
      if (this.marketPage !== 1) this.$emit('change-market-page', 1);
    },
    marketKeyword() {
      if (this.marketPage !== 1) this.$emit('change-market-page', 1);
    },
    selectedElement() {
      if (this.marketPage !== 1) this.$emit('change-market-page', 1);
    },
    minLevelInput() {
      if (this.marketPage !== 1) this.$emit('change-market-page', 1);
    },
    selectedKind() {
      if (this.marketPage !== 1) this.$emit('change-market-page', 1);
    },
    marketList() {
      this.syncPublicCountdowns();
    },
    marketTotalPages(pageCount) {
      if (this.marketPage > pageCount) {
        this.$emit('change-market-page', pageCount);
      }
    }
  },
  mounted() {
    this.syncPublicCountdowns();
    this.countdownTimer = setInterval(() => {
      this.nowTs = Date.now();
    }, 1000);
  },
  beforeDestroy() {
    if (this.countdownTimer) {
      clearInterval(this.countdownTimer);
      this.countdownTimer = null;
    }
  },
  methods: {
    getSuitEffectPieceLabel(desc, fallback) {
      const text = String(desc || '');
      const match = text.match(/([二两三四五六])件套/);
      const map = { '二': '2件', '两': '2件', '三': '3件', '四': '4件', '五': '5件', '六': '6件' };
      return match ? map[match[1]] || fallback : fallback;
    },
    syncPublicCountdowns() {
      const nextMap = {};
      (this.marketList || []).forEach((item) => {
        if (!item || !item.busId) return;
        const remain = Math.max(0, Number(item.remainingPublicSeconds || 0));
        nextMap[item.busId] = {
          seconds: remain,
          fetchedAt: Date.now()
        };
      });
      this.publicCountdownMap = nextMap;
    },
    getPublicRemainSeconds(item) {
      if (!item || !item.busId) return 0;
      const record = this.publicCountdownMap[item.busId];
      if (!record) {
        return Math.max(0, Number(item.remainingPublicSeconds || 0));
      }
      const elapsed = Math.floor((this.nowTs - record.fetchedAt) / 1000);
      return Math.max(0, record.seconds - elapsed);
    },
    changeMarketPage(page) {
      const nextPage = Math.max(1, Math.min(this.marketTotalPages, Number(page) || 1));
      if (nextPage !== this.marketPage) {
        this.$emit('change-market-page', nextPage);
      }
    },
    resetMarketFilters() {
      this.marketKeyword = "";
      this.selectedElement = "all";
      this.selectedKind = "all";
      this.minLevelInput = "";
      if (this.marketPage !== 1) {
        this.$emit('change-market-page', 1);
      }
    },
    refreshCurrentTab() {
      if ((this.currentTab === 'daily' || this.currentTab === 'treasure') && this.$refs.embeddedCommerce && typeof this.$refs.embeddedCommerce.refreshAll === 'function') {
        this.$refs.embeddedCommerce.refreshAll();
        return;
      }
      this.$emit('refresh-market');
    },
    handleSlotClick(slot) {
      if (slot.item) return;
      this.$emit("create-stall", slot.slot);
    },
    findStallBySlot(slot) {
      const list = this.activeStalls || [];
      const matched = list.find((item) => this.extractStallSlot(item) === slot);
      if (matched) {
        return matched;
      }
      return list[slot - 1] || null;
    },
    extractStallSlot(item) {
      if (!item) return null;
      const keys = [
        "stallSlot",
        "stallNo",
        "slot",
        "slotNo",
        "boothSlot",
        "boothNo",
        "stallIndex",
        "busSlot"
      ];
      for (const key of keys) {
        const value = Number(item[key]);
        if (Number.isInteger(value) && value > 0) {
          return value;
        }
      }
      return null;
    },
    distinguishColor(color) {
      const colorMap = {
        1: '#8c94a4',
        2: '#879f1e',
        3: '#078599',
        4: '#036cbe',
        5: '#801dae',
        6: '#9e0e42',
        7: '#ff8c00'
      };
      return colorMap[color] || '#172033';
    },
    marketWildXinfaColor(wildXinfaVo) {
      return Number((wildXinfaVo && wildXinfaVo.quality) || 1) + 4;
    },
    marketItemNameColor(item) {
      if (this.isWildXinfaItem(item)) {
        return '#b45309';
      }
      if (this.isEquipItem(item) && item.packItemsVo) {
        return this.distinguishColor(item.packItemsVo.color);
      }
      return this.distinguishColor(item.color);
    },
    marketItemTagText(item) {
      if (this.isWildXinfaItem(item)) return '心法';
      if (this.isEquipItem(item)) return '装备';
      return '物品';
    },
    marketItemTagColor(item) {
      if (this.isWildXinfaItem(item)) return 'default';
      if (this.isEquipItem(item)) return 'purple';
      return 'blue';
    },
    marketItemMeta(item) {
      if (this.isWildXinfaItem(item)) {
        return `心法 · 数量 x ${item.goodNum || 0}`;
      }
      if (this.isEquipItem(item)) {
        return '';
      }
      return `${item.goodName || '物品'} · 数量 x ${item.goodNum || 0}`;
    },
    marketItemStats(item) {
      if (this.isWildXinfaItem(item)) {
        const info = this.getWildXinfaInfo(item);
        return [
          info.maxLayer ? `${info.maxLayer}层` : null,
          info.affixCountPerLayer ? `每层${info.affixCountPerLayer}条` : null
        ].filter(Boolean).join(' · ');
      }
      if (!this.isEquipItem(item)) return '';
      const info = item.packItemsVo || {};
      return [
        info.weaponTypeDec || info.element,
        this.formatMarketStat('生命', this.firstMarketValue(info, ['life'])),
        this.formatMarketStat('力道攻', this.firstMarketValue(info, ['attack', 'physicalAttack'])),
        this.formatMarketStat('灵力攻', this.firstMarketValue(info, ['magAttack', 'magicAttack'])),
        this.formatMarketStat('力道防', this.firstMarketValue(info, ['defense'])),
        this.formatMarketStat('灵力防', this.firstMarketValue(info, ['magicDefense'])),
        this.formatMarketStat('根骨', this.firstMarketValue(info, ['physique'])),
        this.formatMarketStat('力道', this.firstMarketValue(info, ['dexterous'])),
        this.formatMarketStat('灵力', this.firstMarketValue(info, ['spirit'])),
        this.formatMarketStat('速度', this.firstMarketValue(info, ['speed'])),
        this.formatMarketStat('暴击值', this.firstMarketValue(info, ['criticalLevel'])),
        this.formatMarketStat('抗暴值', this.firstMarketValue(info, ['criticalResistLevel'])),
        this.formatMarketPercentStat('暴击率', this.firstMarketValue(info, ['critical'])),
        this.formatMarketPercentStat('暴击伤害', this.firstMarketValue(info, ['criDamage'])),
        this.formatMarketStat('破甲值', this.firstMarketValue(info, ['armorNumber'])),
        this.formatMarketPercentStat('破甲率', this.firstMarketValue(info, ['armor'])),
        this.formatMarketStat('连击值', this.firstMarketValue(info, ['comboNumber'])),
        this.formatMarketPercentStat('连击伤害', this.firstMarketValue(info, ['combo'])),
        this.formatMarketStat('反击值', this.firstMarketValue(info, ['counterNumber'])),
        this.formatMarketPercentStat('反击伤害', this.firstMarketValue(info, ['counter'])),
        this.formatMarketStat('持续值', this.firstMarketValue(info, ['botNumber'])),
        this.formatMarketPercentStat('持续伤害', this.firstMarketValue(info, ['bot'])),
        this.formatMarketPercentStat('命中', this.firstMarketValue(info, ['hitRate'])),
        this.formatMarketPercentStat('闪避', this.firstMarketValue(info, ['evade'])),
        this.formatMarketPercentStat('抗暴率', this.firstMarketValue(info, ['criticalRegist'])),
        this.formatMarketStat('福源', this.firstMarketValue(info, ['lucky'])),
        this.formatMarketPercentStat('PVE增伤', this.firstMarketValue(info, ['pveDamageRatio'])),
        this.formatMarketPercentStat('PVE减伤', this.firstMarketValue(info, ['pveReduceRatio'])),
        this.formatMarketPercentStat('PVP增伤', this.firstMarketValue(info, ['pvpDamageRatio'])),
        this.formatMarketPercentStat('PVP减伤', this.firstMarketValue(info, ['pvpReduceRatio']))
      ].filter(Boolean).join(' · ');
    },
    marketItemAffixes(item) {
      const info = item && item.packItemsVo;
      return info && Array.isArray(info.affixVoList) ? info.affixVoList : [];
    },
    marketItemSuit(item) {
      const info = item && item.packItemsVo;
      if (!info || !info.suitName) return '';
      return `套装：${info.suitName}`;
    },
    firstMarketValue(item, keys) {
      for (const key of keys) {
        if (item && item[key] !== null && item[key] !== undefined && item[key] !== '' && Number(item[key]) !== 0) {
          return item[key];
        }
      }
      return null;
    },
    formatMarketStat(label, value) {
      if (value === null || value === undefined || value === '' || Number(value) === 0) return '';
      return `${label} ${value > 0 ? '+' : ''}${value}`;
    },
    formatMarketPercentStat(label, value) {
      if (value === null || value === undefined || value === '' || Number(value) === 0) return '';
      return `${label} +${this.formatPercent(value)}`;
    },
    marketDetailStats(item) {
      const fields = [
        ['等级', item.level ? `Lv.${item.level}` : '未标注'],
        ['元素', item.weaponTypeDec || item.element || '无'],
        ['类型', item.typeDec || '装备'],
        ['生命', item.life],
        ['力道攻击', item.attack !== undefined ? item.attack : item.physicalAttack],
        ['灵力攻击', item.magAttack !== undefined ? item.magAttack : item.magicAttack],
        ['力道防御', item.defense],
        ['灵力防御', item.magicDefense],
        ['根骨', item.physique],
        ['力道', item.dexterous],
        ['灵力', item.spirit],
        ['速度', item.speed],
        ['暴击值', item.criticalLevel],
        ['抗暴值', item.criticalResistLevel],
        ['破甲值', item.armorNumber],
        ['连击值', item.comboNumber],
        ['反击值', item.counterNumber],
        ['持续值', item.botNumber],
        ['暴击率', this.formatMarketPercentValue(item.critical)],
        ['暴伤', this.formatMarketPercentValue(item.criDamage)],
        ['破甲率', this.formatMarketPercentValue(item.armor)],
        ['连击伤害', this.formatMarketPercentValue(item.combo)],
        ['反击伤害', this.formatMarketPercentValue(item.counter)],
        ['持续伤害', this.formatMarketPercentValue(item.bot)],
        ['命中', this.formatMarketPercentValue(item.hitRate)],
        ['闪避', this.formatMarketPercentValue(item.evade)],
        ['抗暴率', this.formatMarketPercentValue(item.criticalRegist)],
        ['福源', item.lucky],
        ['PVE增伤', this.formatMarketPercentValue(item.pveDamageRatio)],
        ['PVE减伤', this.formatMarketPercentValue(item.pveReduceRatio)],
        ['PVP增伤', this.formatMarketPercentValue(item.pvpDamageRatio)],
        ['PVP减伤', this.formatMarketPercentValue(item.pvpReduceRatio)]
      ];
      return fields
        .filter((field) => field[1] !== null && field[1] !== undefined && field[1] !== '' && (typeof field[1] !== 'number' || field[1] !== 0))
        .map((field) => ({ label: field[0], value: field[1] }));
    },
    formatMarketPercentValue(value) {
      if (value === null || value === undefined || value === '' || Number(value) === 0) return '';
      return this.formatPercent(value);
    },
    formatXinfaAffixLines(affix) {
      const text = String((affix && affix.affixDesc) || '暂无描述').trim();
      // 3/6/9 层通常是整段效果，其他层的多个词条按逗号展开阅读。
      if ([3, 6, 9].includes(Number(affix && affix.layerNo))) return [text];
      return text.split(/[，,]/).map((line) => line.trim()).filter(Boolean);
    },
    openMarketDetail(item) {
      this.selectedMarketItem = item;
      this.marketDetailVisible = true;
    },
    isEquipItem(item) {
      return !!(item && !this.isWildXinfaItem(item) && (((item.goodId && String(item.goodId).length >= 10) || item.packItemsVo)));
    },
    isWildXinfaItem(item) {
      if (!item) return false;
      if (item.wildXinfaVo) return true;

      const packInfo = item.packItemsVo || {};
      const values = [
        item.itemType,
        item.goodType,
        item.type,
        item.typeDec,
        item.itemTypeDec,
        item.goodName,
        item.busName,
        item.itemName,
        packInfo.itemType,
        packInfo.type,
        packInfo.typeDec,
        packInfo.itemTypeDec,
        packInfo.itemName
      ].filter(value => value !== null && value !== undefined).map(value => String(value));

      if (values.some(value => value === '98' || /野外心法|心法/.test(value))) return true;

      const name = String(item.busName || item.goodName || item.itemName || '');
      return /心法|诀$|经$|典$|谱$|录$/.test(name);
    },
    getWildXinfaInfo(item) {
      if (!item) return {};
      if (item.wildXinfaVo) return item.wildXinfaVo;
      return {
        xinfaName: item.busName || item.goodName || item.itemName,
        quality: item.quality || item.color,
        qualityName: item.qualityName || item.ship,
        maxLayer: item.maxLayer,
        affixCountPerLayer: item.affixCountPerLayer,
        identifyStatus: item.identifyStatus,
        affixes: Array.isArray(item.affixes) ? item.affixes : []
      };
    },
    formatPublicRemain(seconds) {
      const remain = Math.max(0, Number(seconds || 0));
      const minutes = Math.floor(remain / 60);
      const secs = remain % 60;
      return `${minutes}分${secs}秒`;
    },
    formatPercent(val) {
      return ((Number(val || 0)) * 100).toFixed(1) + '%';
    },
    hasAdvancedStats(item) {
      if (!item) return false;
      return [
        'criticalLevel', 'criticalResistLevel', 'critical', 'criDamage',
        'armorNumber', 'armor', 'comboNumber', 'combo',
        'counterNumber', 'counter', 'botNumber', 'bot',
        'hitRate', 'evade', 'criticalRegist',
        'speed', 'lucky', 'pveDamageRatio', 'pveReduceRatio',
        'pvpDamageRatio', 'pvpReduceRatio'
      ].some(field => {
        const raw = item[field];
        if (raw === null || raw === undefined || raw === '') return false;
        const num = Number(raw);
        return Number.isFinite(num) ? num !== 0 : Boolean(raw);
      });
    },
    affixColor(level) {
      const map = {
        1: '#8c94a4',
        2: '#879f1e',
        3: '#078599',
        4: '#036cbe',
        5: '#801dae',
        6: '#9e0e42',
        7: '#ff8c00'
      };
      return map[level] || '#515a6e';
    },
    formatOrderStatus(status) {
      const map = {
        1: "待揭榜",
        2: "待交付",
        3: "待领取",
        4: "已完成",
        5: "已过期"
      };
      return map[Number(status)] || "未知";
    },
    canDeliverAcceptedOrder(item) {
      return Number(item && item.busStatus) === 2;
    },
    canClaimPostedOrder(item) {
      const status = Number(item && item.busStatus);
      return status === 3 || status === 5;
    }
  }
};
</script>

<style scoped>
:deep(.fang-market-popper .ivu-poptip-inner) {
  width: auto !important;
  min-width: 240px !important;
  max-width: min(92vw, 320px) !important;
  border-radius: 10px;
}

:deep(.fang-market-popper .ivu-poptip-body) {
  padding: 12px !important;
}

.fang-market-panel {
  padding: 8px 0 0;
  background: #fff;
  color: #515a6e;
}
.market-head {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
}
.market-title-wrap h3 {
  margin: 4px 0 6px;
  font-size: 20px;
  color: #17233d;
}
.market-title-wrap p {
  margin: 0;
  color: #808695;
  font-size: 13px;
}
.market-tag {
  display: inline-block;
  font-size: 12px;
  color: #2d8cf0;
  letter-spacing: 2px;
}
.market-stat {
  display: flex;
  gap: 10px;
}
.stat-card {
  min-width: 120px;
  padding: 10px 14px;
  background: #f8f8f9;
  border: 1px solid #e8eaec;
  border-radius: 8px;
  text-align: center;
}
.stat-card span {
  display: block;
  font-size: 12px;
  color: #808695;
}
.stat-card strong {
  font-size: 18px;
  color: #17233d;
}
.tab-row {
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
  padding-bottom: 10px;
  border-bottom: 1px solid #e8eaec;
}
.tab-btn {
  padding: 8px 14px;
  border: 1px solid #dcdee2;
  border-radius: 6px;
  background: #fff;
  color: #515a6e;
  cursor: pointer;
}
.tab-btn.active {
  border-color: #2d8cf0;
  color: #2d8cf0;
  background: #f0faff;
}
.market-section {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.market-filter-bar {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 14px 16px;
  border: 1px solid #e8eaec;
  border-radius: 10px;
  background: linear-gradient(135deg, rgba(240, 250, 255, 0.88) 0%, #ffffff 52%);
}
.market-filter-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px 16px;
}
.market-filter-group {
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 36px;
}
.market-filter-group--search {
  flex: 1 1 260px;
  max-width: 240px;
}
.market-filter-label {
  color: #808695;
  font-size: 12px;
  letter-spacing: 1px;
  white-space: nowrap;
}
.market-filter-input {
  height: 36px;
  padding: 0 12px;
  border: 1px solid #dcdee2;
  border-radius: 8px;
  background: #fff;
  color: #515a6e;
  outline: none;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}
.market-filter-input:focus {
  border-color: #57a3f3;
  box-shadow: 0 0 0 3px rgba(45, 140, 240, 0.12);
}
.market-filter-input--level {
  width: 110px;
}
.element-filter-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.element-filter-chip {
  min-width: 42px;
  height: 32px;
  padding: 0 10px;
  border: 1px solid #d7e7f8;
  border-radius: 999px;
  background: #f7fbff;
  color: #5c6b7a;
}
.element-filter-chip.active {
  border-color: #2d8cf0;
  background: linear-gradient(135deg, #2d8cf0 0%, #69b1ff 100%);
  color: #fff;
}
.market-filter-reset {
  height: 36px;
}
.market-filter-summary {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 18px;
  padding: 0 4px;
  color: #808695;
  font-size: 12px;
}
.market-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  grid-auto-rows: minmax(0, 1fr);
  min-height: 0;
  gap: 8px;
}
.market-content-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}
.market-stat--inline {
  display: flex;
  align-items: baseline;
  gap: 4px;
  padding: 4px 7px;
  color: #6f8da6;
  background: #f7fbff;
  border: 1px solid #c9dced;
  border-radius: 4px;
  font-size: 10px;
}
.market-stat--inline strong { color: #3978b7; font-size: 11px; }
.market-card {
  padding: 12px;
  border: 1px solid #e8eaec;
  border-radius: 8px;
  background: #fff;
}
.market-card.is-xinfa {
  border-color: #f1d39a;
  background: linear-gradient(180deg, #fffaf0 0%, #ffffff 100%);
}
.market-card__top,
.market-card__bottom {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.market-card__top {
  margin-bottom: 10px;
}
.market-card__seller,
.market-card__meta {
  color: #808695;
  font-size: 12px;
}
.market-card__meta--warn {
  color: #d46b08;
}
.market-card__name {
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 8px;
  word-break: break-all;
}
.market-card__price,
.trade-price,
.stall-price {
  color: #2d8cf0;
  font-weight: 600;
}
.market-equip-pop {
  min-width: 220px;
  width: 220px;
  line-height: 1.7;
}
.pop-title {
  font-weight: 700;
  margin-bottom: 8px;
}
.pop-row {
  color: #515a6e;
  font-size: 13px;
}
.pop-desc {
  margin-top: 8px;
  color: #808695;
  font-size: 12px;
  max-width: 260px;
  white-space: normal;
  word-break: break-all;
  overflow-wrap: anywhere;
  line-height: 1.6;
}
.pop-section {
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px dashed #e8eaec;
  max-width: 260px;
}
.pop-section__title {
  margin-bottom: 4px;
  color: #17233d;
  font-size: 12px;
  font-weight: 600;
}
.affix-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px 10px;
}
.affix-item {
  padding: 6px 8px;
  border-radius: 6px;
  background: #f8f8f9;
}
.affix-item__name {
  font-size: 12px;
  font-weight: 600;
  margin-bottom: 2px;
}
.affix-item__desc {
  font-size: 12px;
  line-height: 1.5;
  word-break: break-all;
}
.xinfa-affix-list {
  display: grid;
  gap: 8px;
}
.xinfa-affix-item {
  padding: 8px 10px;
  border-radius: 8px;
  background: #fff7e6;
  border: 1px solid #ffe7ba;
}
.xinfa-affix-item--locked {
  background: #f8f8f9;
  border-color: #e8eaec;
}
.xinfa-affix-item__head {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 4px;
  color: #8c6d1f;
  font-size: 12px;
  font-weight: 600;
}
.xinfa-affix-item--locked .xinfa-affix-item__head {
  color: #808695;
}
.xinfa-affix-item__desc {
  color: #515a6e;
  font-size: 12px;
  line-height: 1.5;
  word-break: break-all;
}
.list-wrap {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.trade-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 16px;
  border: 1px solid #e8eaec;
  border-radius: 8px;
  background: #fff;
}
.trade-main {
  min-width: 0;
}
.trade-title {
  color: #17233d;
  font-weight: 600;
  margin-bottom: 4px;
  font-size: 14px;
}
.trade-meta {
  color: #808695;
  font-size: 13px;
}
.trade-side {
  display: flex;
  align-items: center;
  gap: 10px;
}
.stall-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 12px;
}
.stall-card {
  padding: 14px;
  border: 1px solid #e8eaec;
  border-radius: 8px;
  background: #fff;
  cursor: pointer;
}
.stall-card--empty:hover {
  border-color: #2d8cf0;
  box-shadow: 0 4px 12px rgba(45, 140, 240, 0.08);
}
.stall-title,
.stall-name {
  color: #17233d;
  font-weight: 600;
}
.stall-name {
  margin: 8px 0 6px;
}
.stall-meta,
.stall-empty,
.stall-tip {
  color: #808695;
  font-size: 13px;
}
.stall-tip {
  margin-top: 8px;
}
.empty-state {
  padding: 24px 12px;
  text-align: center;
  border: 1px dashed #dcdee2;
  border-radius: 8px;
  color: #808695;
  background: #fafafa;
}
.grid-empty {
  grid-column: 1 / -1;
}
.market-pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
  padding-top: 4px;
}
.market-pagination__text {
  color: #808695;
  font-size: 13px;
}
.list-toolbar {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 12px;
}
.exchange-manage {
  display: grid;
  gap: 18px;
}
.exchange-block {
  display: grid;
  gap: 10px;
}
.exchange-block__title {
  font-size: 14px;
  font-weight: 600;
  color: #1f2937;
}
.exchange-list {
  display: grid;
  gap: 10px;
}
button {
  padding: 7px 12px;
  border: 1px solid #2d8cf0;
  border-radius: 6px;
  background: #fff;
  color: #2d8cf0;
  cursor: pointer;
}
button:disabled {
  cursor: not-allowed;
  opacity: 0.55;
}
.stop-bubble {
  position: relative;
  z-index: 1;
}
@media (max-width: 768px) {
  .market-head,
  .trade-row {
    flex-direction: column;
    align-items: flex-start;
  }
  .market-filter-bar,
  .market-filter-group {
    width: 100%;
  }
  .market-filter-group {
    align-items: flex-start;
    flex-direction: column;
  }
  .market-filter-input,
  .market-filter-input--level {
    width: 100%;
  }
  .market-filter-reset {
    margin-left: 0;
  }
  .market-stat {
    width: 100%;
  }
  .stat-card {
    flex: 1;
  }
  .trade-side {
    margin-top: 10px;
    width: 100%;
    justify-content: space-between;
  }
  .market-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px;
  }
  .market-card {
    min-width: 0;
    padding: 10px;
  }
  .market-card__top,
  .market-card__bottom {
    gap: 6px;
  }
  .market-card__seller,
  .market-card__meta,
  .market-card__price,
  .market-card__bottom button {
    font-size: 12px;
  }
  .market-card__name {
    font-size: 14px;
    margin-bottom: 6px;
  }
  .list-toolbar {
    justify-content: stretch;
  }
}

@media (max-width: 480px) {
  .market-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px;
  }
}

/* Modern commerce layout: navigation stays stable while the active view scrolls. */
.fang-market-panel {
  padding: 12px 0 0;
  color: #334155;
  background: #fff;
}
.market-head {
  align-items: flex-start;
  margin-bottom: 14px;
  padding: 0 2px;
}
.market-title-wrap h3 {
  margin: 3px 0 4px;
  color: #172033;
  font-size: 21px;
  letter-spacing: 0;
}
.market-title-wrap p {
  color: #64748b;
  font-size: 12px;
}
.market-tag {
  color: #0f766e;
  font-size: 11px;
  letter-spacing: 2px;
  font-weight: 800;
}
.market-stat {
  gap: 8px;
}
.stat-card {
  min-width: 104px;
  padding: 8px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #f8fafc;
  text-align: left;
}
.stat-card span {
  color: #64748b;
  font-size: 11px;
}
.stat-card strong {
  display: block;
  margin-top: 2px;
  color: #172033;
  font-size: 16px;
}
.market-layout {
  display: grid;
  grid-template-columns: 148px minmax(0, 1fr);
  min-height: 350px;
  overflow: hidden;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #f8fafc;
}
.market-side-nav {
  display: grid;
  align-content: start;
  gap: 6px;
  padding: 14px 10px;
  border-right: 1px solid #e2e8f0;
  background: #fbfcfe;
}
.tab-btn {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 36px;
  padding: 0 10px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: #64748b;
  font-size: 12px;
  font-weight: 700;
  text-align: left;
}
.tab-btn:hover {
  background: #eef2f7;
  color: #334155;
}
.tab-btn.active {
  border-color: transparent;
  background: #172033;
  color: #fff;
}
.tab-btn em {
  min-width: 20px;
  padding: 1px 5px;
  border-radius: 10px;
  background: #e2e8f0;
  color: #64748b;
  font-size: 10px;
  font-style: normal;
  text-align: center;
}
.tab-btn.active em {
  background: rgba(255, 255, 255, .16);
  color: #fff;
}
.market-content {
  min-width: 0;
  overflow: auto;
  padding: 14px 16px 18px;
  background: #f8fafc;
}
.market-content-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
}
.market-content-head h4 {
  margin: 0 0 3px;
  color: #172033;
  font-size: 15px;
  font-weight: 800;
}
.market-content-head span {
  color: #64748b;
  font-size: 11px;
}
.market-action-button,
.market-primary-button {
  height: 30px;
  padding: 0 11px;
  border: 1px solid #cbd5e1;
  border-radius: 5px;
  background: #fff;
  color: #334155;
  font-size: 12px;
  font-weight: 700;
}
.market-primary-button {
  border-color: #0f766e;
  background: #0f766e;
  color: #fff;
}
.market-action-button:hover,
.market-filter-reset:hover {
  border-color: #94a3b8;
  background: #f1f5f9;
}
.market-primary-button:hover {
  border-color: #115e59;
  background: #115e59;
}
.market-section {
  gap: 10px;
}
.market-filter-bar {
  gap: 8px;
  padding: 11px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  background: #fff;
}
.market-filter-row {
  gap: 8px 14px;
}
.market-filter-group {
  gap: 8px;
  min-height: 30px;
}
.market-filter-group--search {
  max-width: 260px;
}
.market-filter-label {
  color: #64748b;
  font-size: 11px;
  letter-spacing: 0;
}
.market-filter-input {
  height: 30px;
  padding: 0 9px;
  border: 1px solid #cbd5e1;
  border-radius: 5px;
  color: #334155;
  font-size: 12px;
}
.market-filter-input--level {
  width: 82px;
}
.element-filter-list {
  gap: 5px;
}
.element-filter-chip {
  min-width: 30px;
  height: 26px;
  padding: 0 7px;
  border: 1px solid #dbe3ec;
  border-radius: 4px;
  background: #fff;
  color: #64748b;
  font-size: 11px;
}
.element-filter-chip.active {
  border-color: #0f766e;
  background: #0f766e;
  color: #fff;
}
.market-filter-reset {
  height: 30px;
  padding: 0 10px;
  border: 1px solid #cbd5e1;
  border-radius: 5px;
  color: #475569;
  font-size: 11px;
}
.market-filter-summary {
  padding: 0 2px;
  color: #64748b;
  font-size: 11px;
}
.market-grid {
  grid-template-columns: 1fr;
  gap: 8px;
}
.market-card {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 10px;
  align-items: center;
  min-height: 70px;
  padding: 10px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  background: #fff;
  transition: border-color .15s ease, box-shadow .15s ease;
}
.market-card:hover {
  border-color: #b8c4d3;
  box-shadow: 0 3px 10px rgba(15, 23, 42, .05);
}
.market-card.is-xinfa {
  border-color: #ead7a6;
  background: #fffdf7;
}
.market-card__icon,
.trade-icon,
.stall-card__icon {
  display: grid;
  place-items: center;
  width: 42px;
  height: 42px;
  flex: 0 0 42px;
  border: 1px solid currentColor;
  border-radius: 5px;
  background: #f8fafc;
  font-size: 17px;
  font-weight: 800;
}
.market-card__icon {
  display: none;
}
.market-card__main {
  min-width: 0;
}
.market-card__top {
  margin-bottom: 3px;
}
.market-card__seller,
.market-card__meta {
  color: #64748b;
  font-size: 11px;
}
.market-card__name {
  margin-bottom: 3px;
  overflow: hidden;
  font-size: 14px;
  font-weight: 800;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.market-card__side {
  display: grid;
  justify-items: end;
  gap: 7px;
  min-width: 84px;
}
.market-card__price,
.trade-price,
.stall-price {
  color: #b45309;
  font-size: 12px;
  font-weight: 800;
  white-space: nowrap;
}
.market-card__side button,
.trade-side button,
.stop-bubble {
  height: 28px;
  padding: 0 10px;
  border: 1px solid #0f766e;
  border-radius: 5px;
  background: #fff;
  color: #0f766e;
  font-size: 11px;
  font-weight: 700;
}
.market-card__side button:hover,
.trade-side button:hover,
.stop-bubble:hover {
  background: #ecfdf5;
}
.stall-grid {
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 8px;
}
.stall-card {
  display: flex;
  align-items: center;
  gap: 9px;
  min-height: 70px;
  padding: 10px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  background: #fff;
}
.stall-card--empty {
  cursor: pointer;
}
.stall-card--empty:hover {
  border-color: #0f766e;
  box-shadow: 0 3px 10px rgba(15, 118, 110, .08);
}
.stall-card__slot {
  align-self: flex-start;
  color: #64748b;
  font-size: 10px;
  white-space: nowrap;
}
.stall-card__main {
  display: grid;
  min-width: 0;
  flex: 1;
  gap: 3px;
}
.stall-name {
  overflow: hidden;
  color: #172033;
  font-size: 13px;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.stall-meta,
.stall-empty,
.stall-tip {
  color: #64748b;
  font-size: 11px;
}
.stall-tip {
  margin-top: 0;
}
.list-wrap,
.exchange-manage {
  gap: 10px;
}
.list-toolbar {
  margin-bottom: 0;
}
.trade-row {
  display: grid;
  grid-template-columns: 38px minmax(0, 1fr) auto;
  gap: 10px;
  align-items: center;
  padding: 10px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  background: #fff;
}
.trade-icon {
  width: 38px;
  height: 38px;
  flex-basis: 38px;
  color: #64748b;
  font-size: 15px;
}
.trade-title {
  margin-bottom: 3px;
  color: #172033;
  font-size: 13px;
  font-weight: 800;
}
.trade-meta {
  color: #64748b;
  font-size: 11px;
  line-height: 1.5;
}
.trade-side {
  gap: 8px;
}
.exchange-block {
  gap: 8px;
}
.exchange-block__title {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #172033;
  font-size: 13px;
}
.exchange-block__title span {
  color: #64748b;
  font-size: 10px;
  font-weight: 400;
}
.exchange-list {
  gap: 8px;
}
.empty-state {
  padding: 22px 12px;
  border: 1px dashed #cbd5e1;
  border-radius: 6px;
  background: #fff;
  color: #64748b;
  font-size: 12px;
}
.market-pagination {
  padding-top: 2px;
}
.market-pagination button {
  height: 28px;
  padding: 0 9px;
  border: 1px solid #cbd5e1;
  border-radius: 5px;
  color: #475569;
  font-size: 11px;
}
.market-pagination__text {
  color: #64748b;
  font-size: 11px;
}

@media (max-width: 720px) {
  .market-head {
    gap: 10px;
  }
  .market-stat {
    width: auto;
  }
  .stat-card {
    min-width: 86px;
    padding: 7px 8px;
  }
  .market-layout {
    grid-template-columns: 1fr;
  }
  .market-side-nav {
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 4px;
    padding: 8px;
    border-right: 0;
    border-bottom: 1px solid #e2e8f0;
  }
  .tab-btn {
    justify-content: center;
    min-height: 32px;
    padding: 0 4px;
    font-size: 11px;
  }
  .tab-btn em {
    display: none;
  }
  .market-content {
    padding: 12px 10px 14px;
  }
  .market-content-head {
    align-items: flex-start;
  }
  .market-filter-group--search {
    max-width: none;
  }
  .market-card {
    grid-template-columns: minmax(0, 1fr) auto;
    gap: 7px;
    padding: 8px;
  }
  .market-card__icon {
    width: 36px;
    height: 36px;
    flex-basis: 36px;
    font-size: 14px;
  }
  .market-card__side {
    min-width: 68px;
  }
  .market-card__side button,
  .trade-side button,
  .stop-bubble {
    padding: 0 7px;
  }
  .stall-grid {
    grid-template-columns: 1fr;
  }
  .trade-row {
    grid-template-columns: 34px minmax(0, 1fr);
  }
  .trade-icon {
    width: 34px;
    height: 34px;
    flex-basis: 34px;
  }
  .trade-side {
    grid-column: 2;
    justify-content: space-between;
    width: 100%;
    margin-top: 2px;
  }
}

/* Details use the same restrained commerce language as the modern market panel. */
.market-card__stats {
  overflow: hidden;
  color: #475569;
  font-size: 11px;
  line-height: 1.5;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.market-card__actions {
  display: flex;
  gap: 5px;
}
.market-card__actions button {
  min-width: 40px;
}
.market-card__actions .market-card__view {
  border-color: #cbd5e1;
  color: #475569;
}
.market-card__actions .market-card__view:hover {
  border-color: #94a3b8;
  background: #f8fafc;
}
.market-detail-content {
  color: #334155;
}
.market-detail-summary {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  background: #f8fafc;
}
.market-detail-icon {
  display: grid;
  place-items: center;
  width: 52px;
  height: 52px;
  flex: 0 0 52px;
  border: 1px solid currentColor;
  border-radius: 6px;
  background: #fff;
  font-size: 22px;
  font-weight: 800;
}
.market-detail-summary h4 {
  margin: 0 0 5px;
  font-size: 17px;
  font-weight: 800;
}
.market-detail-summary p {
  margin: 2px 0;
  color: #64748b;
  font-size: 12px;
}
.market-detail-section {
  margin-top: 12px;
}
.market-detail-section__title {
  margin-bottom: 8px;
  color: #172033;
  font-size: 13px;
  font-weight: 800;
}
.market-detail-stat-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 6px;
}
.market-detail-stat {
  display: flex;
  justify-content: space-between;
  gap: 6px;
  min-width: 0;
  padding: 7px 8px;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  background: #fff;
  font-size: 11px;
}
.market-detail-stat span {
  overflow: hidden;
  color: #64748b;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.market-detail-stat strong {
  color: #334155;
  font-weight: 800;
  white-space: nowrap;
}
.market-detail-description,
.market-detail-line {
  margin-top: 8px;
  color: #475569;
  font-size: 12px;
  line-height: 1.7;
  white-space: pre-wrap;
  word-break: break-word;
}
.market-detail-section--nested {
  padding-top: 10px;
  border-top: 1px solid #e2e8f0;
}
.market-detail-affixes {
  display: grid;
  gap: 6px;
}
.market-detail-affix {
  display: grid;
  gap: 2px;
  padding: 7px 8px;
  border-left: 3px solid currentColor;
  background: #f8fafc;
}
.market-detail-affix strong {
  font-size: 12px;
}
.market-detail-affix span {
  color: #475569;
  font-size: 11px;
  line-height: 1.55;
}
.market-detail-xinfa-list {
  display: grid;
  gap: 7px;
}
.market-detail-xinfa-item {
  padding: 8px 9px;
  border: 1px solid #ead7a6;
  border-left: 3px solid #c28b22;
  background: #fffdf7;
}
.market-detail-xinfa-item--locked {
  border-color: #e2e8f0;
  border-left-color: #94a3b8;
  background: #f8fafc;
}
.market-detail-xinfa-head {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 4px;
  color: #8a641c;
  font-size: 12px;
}
.market-detail-xinfa-item--locked .market-detail-xinfa-head {
  color: #64748b;
}
.market-detail-xinfa-line {
  color: #475569;
  font-size: 12px;
  line-height: 1.6;
  word-break: break-word;
}
:deep(.fang-market-popper .ivu-poptip-inner) {
  min-width: 260px !important;
  max-width: min(92vw, 360px) !important;
  padding: 0 !important;
  overflow: hidden;
  border: 1px solid #dbe3ec;
  border-radius: 6px;
  box-shadow: 0 10px 28px rgba(15, 23, 42, .13);
}
:deep(.fang-market-popper .ivu-poptip-body) {
  padding: 12px !important;
  color: #334155;
  background: #fff;
}
.fang-market-popper .market-equip-pop {
  width: auto;
  min-width: 240px;
  line-height: 1.6;
}
.fang-market-popper .pop-title {
  padding-bottom: 7px;
  border-bottom: 1px solid #e2e8f0;
  color: #172033;
  font-size: 14px;
}
.fang-market-popper .pop-row {
  color: #475569;
  font-size: 11px;
}
.fang-market-popper .pop-section {
  max-width: none;
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid #e2e8f0;
}
.fang-market-popper .pop-section__title {
  color: #172033;
  font-size: 11px;
}
.fang-market-popper .affix-item,
.fang-market-popper .xinfa-affix-item {
  border-radius: 4px;
}
.fang-market-popper .xinfa-affix-item {
  padding: 7px 8px;
  border-color: #ead7a6;
}
.fang-market-popper .xinfa-affix-item__desc {
  color: #475569;
  font-size: 11px;
  line-height: 1.55;
}

@media (max-width: 720px) {
  .market-card__actions {
    flex-direction: column;
  }
  .market-detail-stat-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
</style>

<style>
.fang-market-panel .market-grid {
  grid-template-columns: repeat(2, minmax(0, 1fr));
  grid-auto-rows: auto;
  min-height: 0;
}
.fang-market-panel .market-card__main { min-width: 0; }
.fang-market-panel .market-card__stats,
.fang-market-panel .market-card__affixes,
.fang-market-panel .market-card__suit {
  overflow: visible;
  line-height: 1.45;
  white-space: normal;
  word-break: break-word;
}
.fang-market-panel .market-card__stats { color: #426581; font-size: 11px; }
.fang-market-panel .market-card__affixes {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  color: #a27632;
  font-size: 10px;
}
.fang-market-panel .market-card__affixes span { color: inherit; white-space: normal; }
.fang-market-panel .market-card__suit { color: #a27632; font-size: 11px; font-weight: 700; }
.fang-market-panel .market-card__suitline {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 3px 5px;
  color: #a27632;
  font-size: 10px;
  line-height: 1.45;
  white-space: normal;
  word-break: break-word;
}
.fang-market-panel .market-card__suitline span {
  white-space: normal;
}
@media (max-width: 768px) {
  .fang-market-panel .market-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 6px; }
  .fang-market-panel .market-card { min-height: 96px; padding: 8px; }
}
</style>

<style>
.fang-market-panel .market-grid {
  grid-template-columns: minmax(0, 1fr) !important;
  grid-auto-rows: auto !important;
  min-height: 0 !important;
}
.fang-market-panel .market-content-actions { display: flex; align-items: center; gap: 6px; }
@media (max-width: 768px) {
  .fang-market-panel .market-content-actions { flex-wrap: wrap; justify-content: flex-end; }
  .fang-market-panel .market-stat--inline { padding: 3px 5px; font-size: 9px; }
  .fang-market-panel .market-action-button { min-width: 48px; }
}
</style>

<style>
/* Keep the market list as full-width rows instead of a compressed card grid. */
.fang-market-panel .market-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  grid-auto-rows: auto;
  align-content: start;
  min-height: 360px;
  gap: 8px;
}
.fang-market-panel .market-card { width: 100%; min-height: 76px; box-sizing: border-box; }
.fang-market-panel .embedded-commerce-panel { min-width: 0; margin-top: -4px; }
.fang-market-panel .embedded-commerce-panel >>> .farm-market-page { padding: 0; }
.fang-market-panel .embedded-commerce-panel >>> .farm-market-panel { max-height: none; }
.fang-market-panel .market-card.is-xinfa .ivu-tag {
  color: #b45309;
  background: #fff7ed;
  border-color: #fdba74;
}
.fang-market-panel .market-card.is-xinfa {
  border-color: #fdba74;
  background: #fffaf5;
}
.fang-market-panel .market-card.is-xinfa .market-card__meta,
.fang-market-panel .market-card.is-xinfa .market-card__stats,
.fang-market-panel .market-card.is-xinfa .market-card__suitline,
.fang-market-panel .market-card.is-xinfa .market-card__suit {
  color: #b45309;
}
.fang-market-panel .market-card.is-xinfa .market-card__suitline span {
  color: #b45309;
}
@media (max-width: 768px) {
  .fang-market-panel .market-grid { min-height: 420px; }
}
</style>



// WEBPACK FOOTER //
// src/components/FangMarketSelectPanel.vue